<template>
  <div>
    <PageHeaderBanner>
      <template #title>得意先および掛け率一覧</template>
    </PageHeaderBanner>
    <div class="cmd00400-conditions-form">
      <q-card style="background: var(--q-lightGray); margin-bottom: 70px" flat bordered>
        <q-card-section class="q-gutter-y-sm">
          <div class="row q-gutter-x-sm">
            <div class="col-2">
              <label>得意先コード</label>
              <q-input
                v-model="customerCode"
                outlined
                dense
                style="background: var(--q-white)"
                class="input-area"
                maxlength="7"
              />
            </div>
            <div class="col">
              <label>得意先名｜納品場所</label>
              <q-input
                v-model="customerName"
                outlined
                dense
                style="background: var(--q-white)"
                class="input-area"
                maxlength="100"
              />
            </div>
          </div>
          <div class="row">
            <div class="col">
              <q-checkbox v-model="isAllSearch" label="無効データを含む" />
            </div>
            <div class="col q-gutter-x-xs flex justify-end" style="margin-top: 10px">
              <q-btn
                color="primary"
                style="width: 150px"
                label="検索"
                icon="mdi-magnify"
                @click="searchCustomer()"
              />
              <q-btn
                outline
                style="width: 150px; background: var(--q-white) !important; color: var(--q-delete)"
                icon="mdi-eraser"
                label="クリア"
                @click="clearSearchCondition"
              />
            </div>
          </div>
        </q-card-section>
      </q-card>
    </div>
    <div class="cmd00400-result-table">
      <div
        :class="
          'chem-table-area btn-exist table-top-button ' + (rows.length === 0 ? 'table-nodata' : '')
        "
      >
        <q-table
          class="my-sticky-header-column-table"
          :rows="rows"
          :columns="columns"
          row-key="index"
          flat
          dense
          separator="cell"
          :hide-no-data="hideNoDataFlg"
          no-data-label="データがありません。"
          :rows-per-page-options="[10, 20, 50, 0]"
          rows-per-page-label="件表示"
          rows-per-page="20"
          :pagination-label="
            (firstRowIndex, endRowIndex, totalRowsNumber) => {
              return `${totalRowsNumber} 件中 ${firstRowIndex}～${endRowIndex} 件表示`;
            }
          "
        >
          <template #no-data="{ message }">
            <div class="full-width row q-gutter-sm">
              <q-icon size="24px" name="mdi-alert-circle-outline" />
              <span class="q-pt-xs">{{ message }}</span>
            </div>
          </template>
          <template #top>
            <div class="add-btn-area flex items-center filter-select">
              <q-btn
                icon="mdi-plus"
                label="追加"
                class="add-btn"
                color="primary"
                @click="addData"
              />
            </div>
          </template>

          <!-- 編集Header -->
          <template #header-cell-edit>
            <q-th class="table-btn-area"></q-th>
          </template>
          <!-- 編集Body -->
          <template #body-cell-edit="cell">
            <q-td
              v-if="!hideNoDataFlg"
              :class="'table-btn-area ' + (cell.row.is_deleted === '1' ? 'deleted' : '')"
            >
              <div class="flex">
                <q-btn
                  icon="mdi-pencil"
                  label="編集"
                  color="primary"
                  @click="editData(cell.row.customer_code)"
                />
              </div>
            </q-td>
          </template>
          <template #body-cell="cell">
            <q-td
              v-if="!hideNoDataFlg"
              :props="cell"
              :class="cell.row.is_deleted === '1' ? 'deleted' : ''"
              >{{ cell.value }}</q-td
            >
          </template>
        </q-table>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { Loading } from 'quasar';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import DisplayModeCode from '../../common/code/DisplayModeCode';
import axios from '../../common/axios-common';
import ResultStatusCode from '../../common/code/ResultStatusCode';
import HttpResponseStatusCode from '../../common/code/HttpResponseStatusCode';
import PageHeaderBanner from '../../components/banner/PageHeaderBanner.vue';

interface MCustomer {
  customer_code: string;
  customer_name: string;
  is_deleted: string;
  create_timestamp: Date;
  update_timestamp: Date;
  create_user_name: string;
  update_user_name: string;
}

// 検索条件：得意先コード
const customerCode = ref('');
// 検索条件：得意先コード
const customerName = ref('');
// 検索条件：無効データを含む
const isAllSearch = ref(false);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns: Array<any> = [
  {
    name: 'edit',
    field: (row: MCustomer) => row.customer_code,
  },
  {
    name: 'customer_code',
    label: '得意先コード',
    align: 'left',
    field: (row: MCustomer) => row.customer_code,
    style: 'width: 100px',
    sortable: true,
  },
  {
    name: 'customer_name',
    label: '得意先名｜納品場所',
    align: 'left',
    field: (row: MCustomer) => row.customer_name,
    sortable: true,
  },

  {
    name: 'create_user_name',
    label: '登録者',
    align: 'left',
    field: (row: MCustomer) => row.create_user_name,
    sortable: true,
  },

  {
    name: 'create_timestamp',
    label: '登録日時',
    align: 'left',
    field: (row: MCustomer) => row.create_timestamp,
    style: 'width: 170px',
    sortable: true,
  },
  {
    name: 'update_user_name',
    label: '更新者',
    align: 'left',
    field: (row: MCustomer) => row.update_user_name,
    sortable: true,
  },

  {
    name: 'update_timestamp',
    label: '更新日時',
    align: 'left',
    field: (row: MCustomer) => row.update_timestamp,
    style: 'width: 170px',
    sortable: true,
  },
];

const rows = ref([
  {
    customer_code: '',
    customer_name: '',
    is_deleted: '',
    create_user_name: '',
    create_timestamp: '',
    update_user_name: '',
    update_timestamp: '',
  },
]);

const router = useRouter();
// 初期表示読み込み時「データがありません」を非表示にするためのフラグ(falseで表示)
const hideNoDataFlg = ref(true);

// 得意先検索
const searchCustomer = async () => {
  // Loading Start
  Loading.show();
  // 検索条件設定
  const formData = new FormData();
  formData.append('customer_code', customerCode.value);
  formData.append('customer_name', customerName.value);
  formData.append('is_all_search', isAllSearch.value.toString());

  await axios
    .post('/cmd004001/search', formData)
    .then((res) => {
      const { common, customerList } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          rows.value = customerList;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          // エラーメッセージ表示
          router.push({ name: 'error' });
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
      hideNoDataFlg.value = false;
    });
};

// 追加ボタン押下
const addData = () => {
  router.push({ name: 'customerRegist', params: { mode: DisplayModeCode.REGISTER } });
};

// テーブル内編集ボタン押下
const editData = (customerCd: string) => {
  router.push({ name: 'customerEdit', params: { mode: DisplayModeCode.CHANGE, customerCd } });
};

// 検索条件クリア
const clearSearchCondition = () => {
  customerCode.value = '';
  customerName.value = '';
  isAllSearch.value = false;
};

// 初期表示
searchCustomer();
</script>
<style scoped>
.cmd00400-conditions-form {
  width: 70%;
  margin: 0 auto;
}
.cmd00400-result-table {
  width: 90%;
  margin: 0 auto;
}
</style>
